import { Stats } from '@react-three/drei'
import { Canvas } from '@react-three/fiber'
import { WEBGL_DPR } from 'features/Procedure/procedure-model/canvas-performance'
import { SpinnerOverlay } from 'common/components/SpinnerOverlay'
import { useAppSelector } from 'common/hooks/redux'
import { useEvaluateModel } from 'features/Procedure/hooks/useEvaluateModel'
import { useModelScene } from 'features/Procedure/hooks/useModelScene'
import { useSaveDrawing } from 'features/Procedure/hooks/useSaveDrawing'
import { useSceneActions } from 'features/Procedure/hooks/useSceneActions'
import {
  DrawMode,
  type DrawingElement,
  type DrawingElementDescription,
} from 'features/Procedure/state/procedure.type'
import {
  formatDrawingNoteDisplay,
  isUnitsNoteDrawMode,
} from 'features/Procedure/utils/drawingDescription.utils'
import { memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CaptureButton } from '../features/notes/components/capture-button'
import { useCaptureNoteImage } from '../features/notes/hooks/useCaptureNoteImage'
import { ActionButtons } from './action-buttons'
import { ActionHistory } from './action-history'
import Body from './body/body'
import type { CanvasCaptureRef } from './canvas-capture'
import CanvasCapture from './canvas-capture'
import { DrawCustomizer } from './draw-customizer'
import { EditDescriptionDialog } from './element-tooltip/edit-description-dialog'
import { PredefinedPositions } from './predefined-positions'
import { EncounterMessageOverlay } from './scene-skeleton/message-overlay/message-overlay'
import styles from './scene.module.scss'

export type ModelSceneMode = 'default' | 'notes'

type Props = {
  debugMode?: boolean
  selectedPatientId: number | null
  selectedPatientSex?: string | null
  mode?: ModelSceneMode
  isReadOnly?: boolean
  onCanvasCaptureRef?: (ref: React.RefObject<CanvasCaptureRef>) => void
  inactiveProcedureOpacity?: number
}

export const ModelScene = memo(
  ({
    debugMode = false,
    selectedPatientId,
    selectedPatientSex,
    mode = 'default',
    isReadOnly = false,
    onCanvasCaptureRef,
    inactiveProcedureOpacity = 0,
  }: Props) => {
    const {
      canvasCaptureRef,
      bodyRef,
      containerRef,
      drawColor,
      drawWidth,
      isFullscreen,
      visibleElements,
      canUndo,
      canRedo,
      cameraPosition,
      cameraTarget,
      handleColorChange,
      handleWidthChange,
      handleToggleFullscreen,
      handleToggleModel,
      undo,
      redo,
    } = useModelScene()

    const { modelPath, isEvaluated, hasDemographicGender } = useEvaluateModel(selectedPatientSex)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const {
      handleSetDrawMode,
      handleSetSelectedElement,
      handleUpdateDrawingElement,
      handleDeleteDrawingElement,
    } = useSceneActions()

    const selectedElement = useAppSelector((state) => state.procedureReducer.selectedElement)
    const [isDialogOpen, setIsDialogOpen] = useState(false)

    useEffect(() => {
      if (selectedElement) {
        setIsDialogOpen(true)
      }
    }, [selectedElement?.element.id])

    useEffect(() => {
      return () => {
        handleSetSelectedElement(null)
      }
    }, [handleSetSelectedElement])

    const handleChangeDescription = useCallback(
      (description: DrawingElementDescription | undefined) => {
        if (isReadOnly || !selectedElement) return
        const updates: Partial<DrawingElement> = { description }
        if (selectedElement.element.drawMode === DrawMode.FREE_LINE) {
          const hasDescription = formatDrawingNoteDisplay(description).length > 0
          if (!hasDescription) {
            updates.descriptionPosition = undefined
          } else if (selectedElement.tooltipPosition) {
            updates.descriptionPosition = {
              x: selectedElement.tooltipPosition.x,
              y: selectedElement.tooltipPosition.y,
              z: selectedElement.tooltipPosition.z,
            }
          }
        }
        handleUpdateDrawingElement(
          selectedElement.element.clientKey || selectedElement.element.id,
          updates,
        )
      },
      [isReadOnly, selectedElement, handleUpdateDrawingElement],
    )

    const handleDialogClose = useCallback(() => {
      setIsDialogOpen(false)
      handleSetSelectedElement(null)
    }, [handleSetSelectedElement])

    const handleDeleteElement = useCallback(() => {
      if (isReadOnly || !selectedElement) return
      handleDeleteDrawingElement(selectedElement.element.clientKey || selectedElement.element.id)
      handleDialogClose()
    }, [isReadOnly, selectedElement, handleDeleteDrawingElement, handleDialogClose])

    const {
      saveStatus,
      isEncounterSyncing,
      hasRemoteModelVersion,
      autosaveDelayMs,
      increaseAutosaveDelay,
      decreaseAutosaveDelay,
    } = useSaveDrawing({ enableAutosave: true })
    const selectedEncounterUid = useAppSelector(
      (state) => state.procedureReducer.selectedEncounterUid,
    )
    const selectedProcedureId = useAppSelector(
      (state) => state.procedureReducer.selectedProcedure?.id,
    )
    const drawMode = useAppSelector((state) => state.procedureReducer.drawMode)

    const { captureImage, isCapturing, canCapture } = useCaptureNoteImage({
      canvasCaptureRef,
      selectedPatientId,
      selectedEncounterUid,
    })

    const isNotesMode = mode === 'notes'
    const isSceneLocked = isReadOnly
    const isDrawingDisabled = isNotesMode || isSceneLocked
    const isSceneReady = isEvaluated && !isEncounterSyncing
    const sceneVisibleElements = useMemo(() => {
      if (!isNotesMode) {
        return visibleElements
      }

      if (!selectedProcedureId) {
        return []
      }

      return visibleElements.filter((element) => element.procedureId === selectedProcedureId)
    }, [isNotesMode, selectedProcedureId, visibleElements])

    useEffect(() => {
      if (isDrawingDisabled && drawMode) {
        handleSetDrawMode(null)
      }
    }, [drawMode, isDrawingDisabled, handleSetDrawMode])

    useEffect(() => {
      if (onCanvasCaptureRef) {
        onCanvasCaptureRef(canvasCaptureRef)
      }
    }, [onCanvasCaptureRef])

    return (
      <div ref={containerRef} className={styles.container}>
        <SpinnerOverlay isLoading={!isSceneReady} />
        <EncounterMessageOverlay show={!selectedEncounterUid && isSceneReady} />
        <>
          <ActionHistory
            hide={isNotesMode}
            onUndo={undo}
            onRedo={redo}
            isUndoDisabled={!canUndo || isSceneLocked}
            isRedoDisabled={!canRedo || isSceneLocked}
            isLoading={saveStatus === 'saving'}
          />
          <PredefinedPositions getPopupContainer={() => containerRef.current || document.body} />
          <ActionButtons
            hide={isNotesMode}
            onToggleFullscreen={handleToggleFullscreen}
            isFullscreen={isFullscreen}
            onToggleModel={handleToggleModel}
            isLoading={!isSceneReady}
            disableActions={isSceneLocked}
            saveStatus={saveStatus}
            hasRemoteModelVersion={hasRemoteModelVersion}
            hasDemographicGender={hasDemographicGender}
            autosaveDelayMs={autosaveDelayMs}
            onIncreaseAutosaveDelay={increaseAutosaveDelay}
            onDecreaseAutosaveDelay={decreaseAutosaveDelay}
            selectedPatientId={selectedPatientId}
          />
          {isNotesMode && !isSceneLocked && (
            <CaptureButton onClick={captureImage} disabled={!canCapture} loading={isCapturing} />
          )}
          <DrawCustomizer
            hide={isNotesMode || isSceneLocked}
            drawColor={drawColor}
            drawWidth={drawWidth}
            onColorChange={handleColorChange}
            onWidthChange={handleWidthChange}
          />
        </>
        {isSceneReady && modelPath && (
          <Canvas
            ref={canvasRef}
            className={styles.canvas}
            dpr={WEBGL_DPR}
            frameloop='demand'
            camera={{
              position: cameraPosition,
              fov: 40,
            }}>
            {debugMode && <Stats showPanel={2} className={styles.stats} />}
            <Suspense>
              <Body
                canvasRef={canvasRef}
                ref={bodyRef}
                debugMode={debugMode}
                drawingElements={sceneVisibleElements}
                modelPath={modelPath}
                cameraPosition={cameraPosition}
                cameraTarget={cameraTarget}
                skinColor='#FFDBAC'
                drawColor={drawColor}
                drawWidth={drawWidth}
                disableDrawing={isDrawingDisabled}
                isNotesMode={isNotesMode}
                readOnly={isSceneLocked}
                inactiveProcedureOpacity={inactiveProcedureOpacity}
              />
            </Suspense>
            <CanvasCapture ref={canvasCaptureRef} />
          </Canvas>
        )}
        <EditDescriptionDialog
          isOpen={isDialogOpen}
          description={selectedElement?.element.description}
          unitsNoteMode={isUnitsNoteDrawMode(selectedElement?.element.drawMode)}
          onSave={handleChangeDescription}
          onDelete={!isReadOnly ? handleDeleteElement : undefined}
          onClose={handleDialogClose}
          readOnly={isReadOnly}
        />
      </div>
    )
  },
)

ModelScene.displayName = 'ModelScene'
